<template>
    <form @submit.prevent="submit">
        <input type="hidden" name="_token" :value="csrfToken">

        <div class="alert alert-danger">
            <ul>
                <li
                    v-for="(value, key) in validationErrors"
                    :key="key"
                    v-html="value"
                />
            </ul>

            <ul>
                <li v-for="error in formErrors.all()" :key="error">{{ error }}</li>
            </ul>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">1.</span> Persoonlijke informatie
            </div>
            <div class="box-body">
                <DateInput
                    v-model="created_at"
                    name="created_at"
                    label="Order datum"
                    min-date="today"
                    v-validate="'required'"
                    type="text"
                    :enable-time="true"
                    :error="formErrors.first('created_at')"
                />
            </div>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">2.</span> Klant informatie
            </div>
            <div class="box-body">
                <CustomerInput
                    v-model="customer_id"
                    name="customer_id"
                    field-label="Klant"
                    initial="Selecteer of voeg toe..."
                    v-validate="'required'"
                    :pre-selected="this.customer_id"
                    :error="formErrors.first('customer_id')"
                    @selected="customerSelected"
                />
            </div>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">3.</span> Adresgegevens
            </div>
            <div class="box-body">
                <div>
                    <h3 class="text-gray-300 font-bold mb-6 ml-48">
                        {{ invoice_addressTitle }}
                    </h3>
                    <TextInput
                        v-model="invoice_address.street"
                        name="invoice_address.street"
                        label="Straatnaam"
                        v-validate="'required'"
                        type="text"
                        class="mb-4"
                        :error="formErrors.first('invoice_address.street')"
                    />
                    <div class="flex mb-4">
                        <TextInput
                            v-model="invoice_address.number"
                            name="invoice_address.number"
                            label="Huisnummer"
                            v-validate="'required'"
                            type="text"
                            :error="formErrors.first('invoice_address.number')"
                        />
                        <TextInput
                            v-model="invoice_address.number_suffix"
                            name="invoice_address.number_suffix"
                            label="Huisnummer toevoeging"
                            type="text"
                            class="ml-4"
                            :error="formErrors.first('invoice_address.number_suffix')"
                        />
                    </div>
                    <TextInput
                        v-model="invoice_address.postal_code"
                        name="invoice_address.postal_code"
                        label="Postcode"
                        v-validate="'required'"
                        type="text"
                        class="mb-4"
                        :error="formErrors.first('invoice_address.postal_code')"
                    />
                    <TextInput
                        v-model="invoice_address.city"
                        name="invoice_address.city"
                        label="Stad"
                        v-validate="'required'"
                        type="text"
                        class="mb-4"
                        :error="formErrors.first('invoice_address.city')"
                    />
                    <SelectInput
                        v-model="invoice_address.country"
                        name="invoice_address.country"
                        field-label="Land"
                        label="name"
                        :options="countries"
                        v-validate="'required'"
                        class="mb-4"
                        :error="formErrors.first('invoice_address.country')"
                    />
                    <p-check class="p-svg p-plain p-bigger p-smooth ml-48" v-model="same_delivery_address">
                        <span slot="extra" class="svg"><Icon name="check-circle" slot="extra" /></span>
                        Legadres is hetzelfde als bovenstaande adres
                    </p-check>
                </div>

                <div v-if="!same_delivery_address" class="mt-8 border-t border-gray-100 pt-8">
                    <h3 class="text-gray-300 font-bold mb-6 ml-48">
                        Legadres
                    </h3>
                    <TextInput
                        v-model="delivery_address.street"
                        name="delivery_address.street"
                        label="Straatnaam"
                        v-validate="'required'"
                        type="text"
                        class="mb-4"
                        :error="formErrors.first('delivery_address.street')"
                    />
                    <div class="flex mb-4">
                        <TextInput
                            v-model="delivery_address.number"
                            name="delivery_address.number"
                            label="Huisnummer"
                            v-validate="'required'"
                            type="text"
                            :error="formErrors.first('delivery_address.number')"
                        />
                        <TextInput
                            v-model="delivery_address.number_suffix"
                            name="delivery_address.number_suffix"
                            label="Huisnummer toevoeging"
                            type="text"
                            class="ml-4"
                            :error="formErrors.first('delivery_address.number_suffix')"
                        />
                    </div>
                    <TextInput
                        v-model="delivery_address.postal_code"
                        name="delivery_address.postal_code"
                        label="Postcode"
                        v-validate="'required'"
                        type="text"
                        class="mb-4"
                        :error="formErrors.first('delivery_address.postal_code')"
                    />
                    <TextInput
                        v-model="delivery_address.city"
                        name="delivery_address.city"
                        label="Stad"
                        v-validate="'required'"
                        type="text"
                        class="mb-4"
                        :error="formErrors.first('delivery_address.city')"
                    />
                    <SelectInput
                        v-model="delivery_address.country"
                        name="delivery_address[country]"
                        field-label="Land"
                        label="name"
                        :options="countries"
                        validation="required"
                    />
                </div>
            </div>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">3.</span> Datum informatie
            </div>
            <div class="box-body">
                <DateInput
                    v-model="delivery_at"
                    name="delivery_at"
                    label="Bezorgdatum"
                    min-date="today"
                    type="text"
                    class="mb-4"
                    :error="formErrors.first('delivery_at')"
                />
                <div class="flex items-center mb-4">
                    <TimeInput
                        v-model="delivery_start_at"
                        name="delivery_start_at"
                        label="Bezorgmoment"
                        min-date="today"
                        type="text"
                        :error="formErrors.first('delivery_start_at')"
                    />
                    <span class="mx-2">-</span>
                    <TimeInput
                        v-model="delivery_end_at"
                        name="delivery_end_at"
                        min-date="today"
                        type="text"
                        :error="formErrors.first('delivery_end_at')"
                    />
                </div>
                <DateInput
                    v-model="laying_at"
                    name="laying_at"
                    label="Legdatum"
                    min-date="today"
                    type="text"
                    class="mb-4"
                    :error="formErrors.first('laying_at')"
                />
                <DateInput
                    v-model="measure_at"
                    name="measure_at"
                    label="Opmeetdatum"
                    min-date="today"
                    type="text"
                    class="mb-4"
                    :error="formErrors.first('measure_at')"
                />
                <div v-if="measure_at" class="form-group" :class="{'has-error': formErrors.first('measure_type') }">
                    <label for="measure_type" class="label">Opgemeten via</label>
                    <div class="field-group">
                        <p-radio
                            v-model="measure_type"
                            type="radio"
                            name="measure_type"
                            color="info"
                            :value="1"
                        >
                            Ter plaatse gemeten
                        </p-radio>
                        <p-radio
                            v-model="measure_type"
                            type="radio"
                            name="measure_type"
                            color="info"
                            :value="2"
                        >
                            Opgegeven door client
                        </p-radio>
                        <p-radio
                            v-model="measure_type"
                            type="radio"
                            name="measure_type"
                            color="info"
                            :value="3"
                        >
                            Van tekening
                        </p-radio>
                        <p v-if="formErrors.first('measure_type')" class="error">
                            {{ formErrors.first('measure_type') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">4.</span> Ruimtes
            </div>
            <div class="box-body">
                <OrderAreas :areas="orderAreas" />
            </div>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">4.</span> Bestelling informatie
            </div>
            <div class="box-body">
                <OrderRows :old="old.rows" />
            </div>
        </div>

        <div class="box">
            <div class="box-title">
                <span class="box-title-number">5.</span> Overzicht bestelling
            </div>
            <div class="box-body">
                <TextInput
                    v-model="discount_amount"
                    name="discount_amount"
                    label="Korting"
                    v-validate="'required'"
                    type="text"
                    class="max-w-md mb-4"
                    :error="formErrors.first('discount_amount')"
                />

                <div class="form-group mb-4">
                    <label class="label">Totaal exclusief BTW:</label>
                    <div class="field-group">
                        <Price :amount="subtotalPrice" />
                    </div>
                </div>

                <div class="form-group mb-4">
                    <label class="label">BTW 21%</label>
                    <div class="field-group">
                        <Price :amount="taxes" />
                    </div>
                </div>

                <div class="form-group font-bold text-lg border-t pt-4 mb-4">
                    <label class="label">Totaal</label>
                    <div class="field-group">
                        <Price :amount="totalPrice" />
                    </div>
                </div>

                <div class="form-group mb-4">
                    <label class="label">Vooruitbetaling</label>
                    <div class="field-group">
                        <Price :amount="prePay" />
                    </div>
                </div>

                <div class="form-group">
                    <label class="label">Contant voldoen</label>
                    <div class="field-group">
                        <Price :amount="prePay" />
                    </div>
                </div>
            </div>
        </div>

        <button type="submit" class="btn btn-green" :disabled="isLoading">
            {{ isLoading ? 'Order wordt opgeslagen...' : 'Opslaan' }}
        </button>
    </form>
</template>

<script>
    import axios from 'axios';
    import get from 'lodash/get';
    import { mapFields } from 'vuex-map-fields';
    import { mapActions, mapGetters, mapState } from 'vuex';

    import OrderAreas from '@/components/orders/OrderAreas';
    import OrderRows from '@/components/orders/OrderRows';
    import CustomerInput from '@/components/inputs/CustomerInput';
    import DateInput from '@/components/inputs/DateInput';
    import TimeInput from '@/components/inputs/TimeInput';
    import SelectInput from '@/components/inputs/SelectInput';
    import TextInput from '@/components/inputs/TextInput';
    import Price from '@/components/Price';

    export default {
        name: 'OrderForm',
        components: {
            CustomerInput,
            DateInput,
            OrderAreas,
            OrderRows,
            Price,
            SelectInput,
            TextInput,
            TimeInput,
        },
        props: {
            order: {
                type: Object,
                required: false,
                default: () => {},
            },
            old: {
                type: [Object, Array],
                required: false,
                default: null,
            },
            defaultAreas: {
                type: Array,
                required: true,
            },
        },
        data() {
            return {
                areas: get(this.order, 'areas', []),
                countries: [],
                created_at: get(this.order, 'created_at', null),
                customer_id: get(this.order, 'customer_id', null),
                delivery_at: get(this.order, 'delivery_at', null),
                delivery_start_at: get(this.order, 'delivery_start_at', null),
                delivery_end_at: get(this.order, 'delivery_end_at', null),
                delivery_address: {
                    city: get(this.order, 'delivery_address.city', null),
                    country: get(this.order, 'delivery_address.country', null),
                    id: get(this.order, 'delivery_address.id', null),
                    number: get(this.order, 'delivery_address.number', null),
                    number_suffix: get(this.order, 'delivery_address.number_suffix', null),
                    postal_code: get(this.order, 'delivery_address.postal_code', null),
                    street: get(this.order, 'delivery_address.street', null),
                },
                same_delivery_address: true,
                laying_at: get(this.order, 'laying_at', null),
                measure_at: get(this.order, 'measure_at', null),
                measure_type: get(this.order, 'measure_type', null),
                invoice_address: {
                    city: get(this.order, 'invoice_address.city', null),
                    country: get(this.order, 'invoice_address.country', null),
                    id: get(this.order, 'invoice_address.id', null),
                    number: get(this.order, 'invoice_address.number', null),
                    number_suffix: get(this.order, 'invoice_address.number_suffix', null),
                    postal_code: get(this.order, 'invoice_address.postal_code', null),
                    street: get(this.order, 'invoice_address.street', null),
                },
                isLoading: false,
                responseErrors: [],
            };
        },
        created() {
            this.getCountries();
            if (this.order) this.findRows(this.order.id);
            if (!this.areas.length) this.areas = this.defaultAreas;
        },
        computed: {
            ...mapGetters('order', [
                'totalPrice',
                'taxes',
                'prePay',
                'subtotalPrice',
            ]),

            ...mapState({
                rows: state => state.order.rows,
            }),

            ...mapFields('order', ['discount_amount']),

            invoice_addressTitle() {
                if (this.same_delivery_address) {
                    return 'Factuur- en legadres';
                }

                return 'Factuuradres';
            },

            orderAreas() {
                if (this.order) {
                    return this.order.areas;
                }

                return this.areas;
            },

            validationErrors() {
                return Object.values(this.responseErrors).flat();
            },
        },
        methods: {
            ...mapActions('order', [
                'findRows',
            ]),

            async getCountries() {
                const { data } = await axios.get(this.route('api.countries.index'));
                this.countries = data;
            },

            reset() {
                this.invoice_address = {
                    city: null,
                    country_id: null,
                    id: null,
                    number: null,
                    number_suffix: null,
                    postal_code: null,
                    street: null,
                };
            },

            customerSelected(customer) {
                if (customer && customer.invoice_address) {
                    this.invoice_address = {
                        city: customer.invoice_address.city,
                        country_id: customer.invoice_address.country_id,
                        country: {
                            id: customer.invoice_address.country_id,
                            name: customer.invoice_address.country.name,
                        },
                        id: customer.invoice_address.id,
                        number: customer.invoice_address.number,
                        number_suffix: customer.invoice_address.number_suffix,
                        postal_code: customer.invoice_address.postal_code,
                        street: customer.invoice_address.street,
                    };
                } else {
                    this.reset();
                }
            },

            submit() {
                this.$validator.validateAll().then(() => {
                    if (this.order) {
                        this.update();
                    } else {
                        this.create();
                    }
                }).catch(() => {
                    console.log('test');
                });
            },

            async create() {
                try {
                    this.isLoading = true;

                    const { data } = await axios.post(this.route('api.orders.store'), {
                        created_at: this.created_at,
                        customer_id: this.customer_id,
                        invoice_address: this.invoice_address,
                        delivery_address: this.delivery_address,
                        laying_at: this.laying_at,
                        delivery_at: this.delivery_at,
                        delivery_start_at: this.delivery_start_at,
                        delivery_end_at: this.delivery_end_at,
                        measure_at: this.measure_at,
                        measure_type: this.measure_type,
                        same_delivery_address: this.same_delivery_address,
                        rows: this.rows,
                        areas: this.orderAreas,
                        discount_amount: this.discount_amount,
                    });

                    window.location.href = this.route('orders.show', data);
                } catch ({ response }) {
                    this.$setLaravelValidationErrorsFromResponse(response.data);
                    this.responseErrors = response.data.errors;
                } finally {
                    this.isLoading = false;
                }
            },

            async update() {
                try {
                    this.isLoading = true;

                    const { data } = await axios.put(this.route('api.orders.update', this.order), {
                        created_at: this.created_at,
                        customer_id: this.customer_id,
                        invoice_address: this.invoice_address,
                        delivery_address: this.delivery_address,
                        laying_at: this.laying_at,
                        delivery_at: this.delivery_at,
                        delivery_start_at: this.delivery_start_at,
                        delivery_end_at: this.delivery_end_at,
                        measure_at: this.measure_at,
                        measure_type: this.measure_type,
                        same_delivery_address: this.same_delivery_address,
                        rows: this.rows,
                        areas: this.orderAreas,
                        discount_amount: this.discount_amount,
                    });

                    window.location.href = this.route('orders.show', data);
                } catch ({ response }) {
                    this.$setLaravelValidationErrorsFromResponse(response.data);
                    this.responseErrors = response.data.errors;
                } finally {
                    this.isLoading = false;
                }
            },
        },
    };
</script>
